<template>
  <div class="mx-auto my-4">
    <ValidationObserver v-slot="{ invalid, validate, errors }">
      <form name="RideForm" @submit.prevent="validate().then(() => onSubmit(invalid))">
        <div class="container">
          <div class="mb-4">
            <ValidationProvider rules="required" name="date of trip" v-slot="{ errors }">
            <CalendarField
              class="w-1/3 pe-5 pb-2"
              :min="today()"
              :max="maxDate()"
              v-model="formValues.tripDate">
              Date of Trip
            </CalendarField>
            <span
              class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
              name="error-text">
              {{ errors[0] }}
            </span>
            </ValidationProvider>
          </div>
        </div>
          <div class="container">
            <div class="w-1/3">
              <label class="text-left block font-bold">Vendor</label>
              <ValidationProvider rules="required" name="designated vendor" v-slot="{ errors }">
                <v-select class="w-full pb-6" v-model="formValues.vendorDesignated" :options="vendorOptions"
                  label="label" :key="vendor.id" placeholder="Select Vendor" track-by="value"
                  :reduce="vendor => vendor.value" />
                <span class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                  name="error-text">
                  {{ errors[0] }}
                </span>
              </ValidationProvider>
            </div>
          </div>
        <div class="container">
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-7 pb-4">
            <div class="flex flex-col">
              <label class="text-left block font-bold mb-2">Requesting District</label>
                <div class="flex flex-col">
                  <div class="border border-gray-300 rounded px-3 py-2 bg-gray-100">
                    {{ selectedDistrict.label || defaultValues.infoRequestingDistrict?.code || 'N/A' }}
                  </div>
                </div>
            </div>
          </div>
        </div>
        <div class="container">
          <label class="text-left block font-bold">Student Info</label>
          <div class="grid grid-cols-3 gap-7"> 
            <TextField
              class="pb-2 w-full"
              :placeholder="'First Name'"
              v-model="formValues.firstName"
              :disabled="true">
            </TextField>
            <TextField
              class="pb-4 w-full"
              :placeholder="'Last Name'"
              v-model="formValues.lastName"
              :disabled="true">
            </TextField>
          </div>
        </div>
        <div class="container">
          <div class="grid grid-cols-3 gap-7"> 
           <div class="flex flex-col pb-6">
              <label class="text-left block font-bold mb-2">
                Grade
              </label>

              <div class="border border-gray-300 rounded px-3 py-2 bg-gray-100">
                {{ gradeList[defaultValues.grade] || defaultValues.grade }}
              </div>
            </div>
          </div>
        </div>
        <div v-if="needsRouteDirection" class="container mt-4 mb-4">
          <label class="block font-bold mb-2">Route Direction</label>

          <label class="inline-flex items-center mr-6">
            <input type="radio" v-model="formValues.route" value="inbound" />
            <span class="ml-2">Inbound</span>
          </label>

          <label class="inline-flex items-center">
            <input type="radio" v-model="formValues.route" value="outbound" />
            <span class="ml-2">Outbound</span>
          </label>
        </div>
        <div v-if="scheduledReplacementTrip" class="container mt-4 mb-4">
          <label class="flex items-start font-bold">
            <input type="checkbox" v-model="formValues.replacesRegularRoute" class="mt-1" />
            <span class="ml-2">
              This OTR replaces the student's regularly scheduled
              {{ scheduledReplacementTrip.direction }} trip:
              Route {{ scheduledReplacementTrip.route.number }}
              with {{ formatTime(scheduledReplacementTrip.time) }}
              {{ scheduledReplacementTrip.direction === 'inbound' ? 'pickup' : 'dropoff' }}
            </span>
          </label>

          <p class="text-sm text-gray-600 mt-2 ml-6">
            Selecting this option will send a one-time no-ride (OTNR)
            notification to
            {{ regularRouteVendorName || "the student's regular transportation provider" }}
            for {{ formattedTripDate }}.
          </p>
        </div>
        <div class="container mt-6">
          <ValidationProvider name="Pickup Time" rules="required" v-slot="{ errors }">
            <div>
              <TimeField v-model="formValues.pickupTime" class="block pb-6 w-1/5">
                Pickup Time
              </TimeField>

              <span class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                name="error-text">
                {{ errors[0] }}
              </span>
            </div>
          </ValidationProvider>
          <div class="pb-6">
            <label class="text-left block mb-2">
              Pickup Location
            </label>

            <label class="mr-4">
              <input type="radio" value="home" v-model="formValues.pickupLocationType" />
              Home
            </label>

            <label class="mr-4">
              <input type="radio" value="school" v-model="formValues.pickupLocationType" />
              School
            </label>

            <label>
              <input type="radio" value="other" v-model="formValues.pickupLocationType" />
              Other
            </label>
          </div>
          <div>
            <label class="text-left block mb-2">
              Pickup Location Name
            </label>

            <input v-model="formValues.pickupLocationName" type="text"
              class="border border-gray-300 rounded px-3 py-2 w-full"
              placeholder="School, facility, door number, or helpful information" />
          </div>
          <StudentAddress class="pb-4" id="Pickup" label="Pickup Address"
            :defaultValue="pickupDefaultAddress"
            :disabled="formValues.pickupLocationType !== 'other'"
            v-model="formValues.pickupAddress">
          </StudentAddress>
          <div class="pb-6">
            <label class="text-left block mb-2">
              Other Pickup Information
            </label>

            <input v-model="formValues.pickupOtherInformation" type="text"
              class="border border-gray-300 rounded px-3 py-2 w-full" />
          </div>
          <ValidationProvider name="Drop-Off Time" rules="required" v-slot="{ errors }">
            <div>
              <TimeField v-model="formValues.dropoffTime" class="block pb-6 w-1/5">
                Drop-Off Time
              </TimeField>

              <span class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                name="error-text">
                {{ errors[0] }}
              </span>
            </div>
          </ValidationProvider>
          <div class="pb-6">
            <label class="text-left block mb-2">
              Dropoff Location
            </label>

            <label class="mr-4">
              <input type="radio" value="home" v-model="formValues.dropoffLocationType" />
              Home
            </label>

            <label class="mr-4">
              <input type="radio" value="school" v-model="formValues.dropoffLocationType" />
              School
            </label>

            <label>
              <input type="radio" value="other" v-model="formValues.dropoffLocationType" />
              Other
            </label>
          </div>
          <div>
            <label class="text-left block mb-2">
              Dropoff Location Name
            </label>

            <input v-model="formValues.dropoffLocationName" type="text"
              class="border border-gray-300 rounded px-3 py-2 w-full"
              placeholder="School, facility, door number, or helpful information" />
          </div>
          <StudentAddress class="pb-4" id="Dropoff" label="Dropoff Address"
            :defaultValue="dropoffDefaultAddress"
            :disabled="formValues.dropoffLocationType !== 'other'"
            v-model="formValues.dropoffAddress">
          </StudentAddress>
          <div class="pb-6">
            <label class="text-left block mb-2">
              Other Dropoff Information
            </label>

            <input v-model="formValues.dropoffOtherInformation" type="text"
              class="border border-gray-300 rounded px-3 py-2 w-full" />
          </div>
        </div>
        <!-- <div class="container">
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-7 pb-4">
            <div class="flex flex-col">
              <label class="text-left block font-bold mb-2">School or Site Destination Name</label>

              <ValidationProvider rules="required" name="school or program name" v-slot="{ errors }">
                <v-select
                  class="border-gray-300 w-full"
                  v-model="selectedSchool"
                  :options="schoolList"
                  label="label"
                  :disabled="false"
                  @input="setSchool()"
                  placeholder="Please select or type school or site destination name and then PRESS ENTER"
                >
                </v-select>
                <span class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1" name="error-text">
                  {{ errors[0] }}
                </span>
              </ValidationProvider>
            </div>
          </div>
        </div> -->
        <!-- <div class="container">
          <p class="text-left block font-bold mb-2">
            Is this an inbound (to school) request or an outbound (from school) request?
          </p>
          <ValidationProvider rules="required" name="inbound/outbound selection" v-slot="{ errors }">
            <div class="flex items-center">
              <label class="mr-4">
                <input type="radio" v-model="formValues.isInbound" :value="true" />
                Inbound
              </label>
              <label>
                <input type="radio" v-model="formValues.isInbound" :value="false" />
                Outbound
              </label>
            </div>
            <span
              class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
              name="error-text"
            >
              {{ errors[0] }}
            </span>
          </ValidationProvider>
          <div class="mb-4"></div>
        </div> -->
        <div class="container">
          <label class="block font-bold mb-2">
            Notes / Other Instructions for the Vendor
          </label>
          <TextArea 
            v-model="formValues.vendorNotes"
          >
          </TextArea>
        </div>
        <div class="container">
          <div class="grid grid-cols-2 gap-7 mt-4 ">
            <div>
              <label class="text-left block font-bold pb-2">Equipment and Special Needs</label>
              <SpecialNeedsSelection
                class="text-sm font-bold"
                :defaultValues="defaultNeed.specialNeeds"
                v-model="formValues.specialNeeds"
                :disabled="true"
                @input="specialChange"
              >
            </SpecialNeedsSelection>
            </div>
            <div class="font-bold flex flex-col gap-1">
              <label class="text-left block font-bold">Student Release</label>
                <ValidationProvider rules="required" name="student release" v-slot="{ errors }">
                  <RadioField
                    class="text-sm font-bold"
                    :disabled="true"
                    v-model="formValues.studentRelease"
                    :value="defaultValues.studentRelease"
                    :options="studentRelease"
                    :alignment="'col'"
                  >
                  </RadioField>
                  <span
                    class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                    name="error-text">
                    {{ errors[0] }}
                  </span>
                </ValidationProvider>
            </div>
            <div class="container">
              <div v-if="!canApproveOneTimeRide">
                <label class="block font-bold mb-2" style="margin-left: -10px;">CST Supervisor Approving Trip</label>
                <div class="grid grid-cols-1 gap-3 mt-2" style="margin-left: -10px;">
                  <ValidationProvider rules="required" name="CST Supervisor" v-slot="{ errors }">
                    <div v-for="supervisor in formattedSupervisors" :key="supervisor.value">
                      <label :for="supervisor.value" class="text-sm font-bold pb-2">
                        <input
                          type="radio"
                          :id="supervisor.value"
                          v-model="formValues.supervisor"
                          :value="supervisor.value"
                          class="mr-2"
                        />
                        {{ supervisor.label }}
                      </label>
                    </div>
                    <span
                      class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                      name="error-text"
                    >
                      {{ errors[0] }}
                    </span>
                  </ValidationProvider>
                </div>
              </div>
            </div>
            <div class="container">
              <label class="block font-bold mb-2">
                Reason for Request
              </label>
              <ValidationProvider rules="required" name="Reason for Request" v-slot="{ errors }">
                <TextArea 
                  v-model="formValues.requestReason"
                >
                </TextArea>
                <span
                    class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                    name="error-text"
                  >
                    {{ errors[0] }}
                  </span>
              </ValidationProvider>
            </div>
          </div>
          <div class="container">
            <label class="block font-bold mb-2" style="margin-left: -10px;">Where did you receive this request?</label>
            <div class="grid grid-cols-1 gap-3 mt-2" style="margin-left: -10px;">
              <ValidationProvider rules="required" name="notified" v-slot="{ errors }">
                <div v-for="(option, index) in notified" :key="index">
                  <label :for="'notified-' + index" class="text-sm font-bold pb-1">
                    <input
                      type="radio"
                      :id="'notified-' + index"
                      v-model="formValues.notified"
                      :value="option.label"
                      class="mr-2"
                    />
                    {{ option.label }}
                  </label>
                </div>
                <span
                  class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                  name="error-text">
                  {{ errors[0] }}
                </span>
              </ValidationProvider>

              <div v-if="formValues.notified" class="mt-2">
                <label class="block font-bold mb-1">Link to communication:</label>
                <input
                  type="text"
                  v-model="formValues.linkToCommunication"
                  class="border border-gray-300 rounded px-2 py-1 w-full mb-4"
                  placeholder="Enter the communication link here"
                />
              </div>
            </div>
          </div>
          <div class="container">
            <ValidationProvider rules="required" name="notified" v-slot="{ errors }">
              <label class="block font-bold mb-2" style="margin-left: -10px;">
                Who is covering the cost of the OTR?
              </label>
              <div class="text-sm font-bold pb-4" style="margin-left: -10px;">
                <div v-if="!isOtrA">
                  <input
                    type="radio"
                    id="cst_our_fault"
                    name="covering_cost"
                    value="CST--Our Fault"
                    v-model="formValues.coveringCost"
                  />
                  <label for="cst_our_fault" class="ml-2">CST--Our Fault</label>
                </div>
                <div>
                  <input
                    type="radio"
                    id="district_client"
                    name="covering_cost"
                    value="DISTRICT/CLIENT"
                    v-model="formValues.coveringCost"
                  />
                  <label for="district_client" class="ml-2">
                    DISTRICT/CLIENT (AUTHORIZED representative must be listed below.)
                  </label>
                </div>
                <div v-if="!isOtrA">
                  <input
                    type="radio"
                    id="cst_vendor_issue"
                    name="covering_cost"
                    value="CST/VENDOR"
                    v-model="formValues.coveringCost"
                  />
                  <label for="cst_vendor_issue" class="ml-2">
                    CST--Vendor Performance Issue (Vendor must be listed below. Be sure to submit a Non-Performance Report.)
                  </label>
                </div>
                <span
                  class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                  v-if="errors[0]"
                >
                  {{ errors[0] }}
                </span>
              </div>
            </ValidationProvider>
          </div>

          <div v-if="formValues.coveringCost === 'DISTRICT/CLIENT'" class="mt-2">
            <label for="district_rep" class="block font-bold mb-2" style="margin-left: -10px;">
              District/Client Representative Approving Trip
              <span class="block text-sm font-normal pb-2">
                (This must be an AUTHORIZED representative of the district/client--not simply a staff member.)
              </span>
            </label>
            <div class="container mb-" style="margin-left: -10px;">
              <TextArea
                class="pb-4"
                style="margin-left: -15px;"
                v-model="formValues.districtRep"
              >
              </TextArea>
            </div>
          </div>
          <div v-if="formValues.coveringCost === 'CST/VENDOR'" class="mt-2" style ="margin-left:-10px">
            <label for="vendor-dropdown" class="block font-bold mb-4">
              Original Vendor at Fault
              <span class="block text-sm font-normal">
                Specify VENDOR, VEHICLE.
              </span>
            </label>
            <div class="grid grid-cols-3 gap-4 items-center">
              <div>
                <label for="vendor" class="block font-bold mb-2">Vendor</label>
                <SelectionField class="text-sm font-bold pb-4" 
                  v-model="formValues.selectedVendor"
                  @input="onVendorChanged"
                  :options="vendorOptions"
                  label="label"
                  placeholder="Select Vendor" />
              </div>
              <div>
                <label for="vehicle" class="block font-bold mb-2">Vehicle</label>
                <SelectionField
                  class="text-sm font-bold pb-4"
                  v-model="formValues.selectedVehicle"
                  :options="vehicleOptions"
                  label="label" 
                  placeholder="Select Vehicle"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="flex flex-row">
          <span class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
            name="error-text"
            v-if="displayFormHasErrors(errors)">
            There are errors in fields above.
          </span>
        </div>
        <SubmitButton
          class="rounded-md h-10 !shadow m-1"
          type="submit"
          :color="'blue'"
          :size="'md'">
          Submit Form
        </SubmitButton> 
      </form>
    </ValidationObserver>
    <div v-if="displayEndpoint">
      <p>Form Values: {{ formValues }}</p>
      <p>Default Values: {{ defaultValues }}</p>
    </div>
  </div>
</template>
  
<script>
import DropdownLists from '../components/DropdownLists.json'
import CalendarField from '../components/formComponents/CalendarField.vue'
import TextField from './formComponents/TextField.vue'
import SelectionField from './formComponents/SelectionField.vue'
import StudentAddress from '../components/StudentAddress.vue'
import TimeField from './formComponents/TimeField.vue'
import SpecialNeedsSelection from './SpecialNeedsSelection.vue'
import RadioField from './formComponents/RadioField.vue'
import TextArea from './formComponents/TextArea.vue'
import SubmitButton from '../components/SubmitButton.vue'

import user from '../services/user/user.js'
import vendors from '../services/vendors.js'

import oneTimeRideFormService from '../services/pages/oneTimeRideForm.js'

import '../services/validation.js'
import { getRouteInfo } from '../services/api.js'

export default {
  components: {
    CalendarField,
    TextField,
    SelectionField,
    StudentAddress,
    TimeField,
    SpecialNeedsSelection,
    RadioField,
    TextArea,
    SubmitButton,
  },
  props: {
    defaultValues: {
      type: Object,
      default: () => ({}),
    },
    gradeList: {
      type: Object,
      required: true
    },
    studentDistrictSelectionOptions: {
      type: Array,
      default: undefined,
    },
  },
  data() {
    return {
      displayEndpoint: process.env.NODE_ENV !== 'production',

      districtOptions: this.districtOptions || [],
      studentDistrictList: this.districtOptions || [],
      districtSelectionType: '',
      currentAddress: '',
      currentAddressType: '',
      vendor: '',
      defaultNeed: {},
      canSubmit: true,
      notified: [
        { value: "slack", label: "Slack" },
        { value: "email_drag", label: "Drag/Email" },
        { value: "phone_message", label: "Phone Message" },
        { value: "other", label: "Other" },
      ],
      isOtrA: false,
      student: {},
      pickupDefaultAddress: {},
      dropoffDefaultAddress: {},
      formValues: {
        alternateServiceProvider: false,
        pickupLocationName: '',
        pickupLocationType: 'home',
        pickupAddress: {},
        pickupOtherInformation: '',

        dropoffLocationName: '',
        dropoffLocationType: 'school',
        dropoffAddress: {},
        dropoffOtherInformation: '',
        requestingDistrict: null,
        tripDate: '',
        isInbound: null,
        replacesRegularRoute: false,
        vendorDesignated: '',
        firstName: '',
        lastName: '',
        grade: this.selectedGrade || '',
        school: '',
        pickupTime: '',
        dropoffTime: '',
        vendorNotes: '',
        specialNeeds: '',
        studentRelease: '',
        supervisor: '',
        requestReason:'',
        notified:'',
        linkToCommunication:'',
        coveringCost:'',
        districtRep:'',
        selectedVendor:'',
        selectedVehicle:'',
        route:'',
      },
      schoolList: [],
      districtList: [],
      selectedDistrict: {},
      specialNeeds: [],
      specialNeedsOptions: DropdownLists.specialNeeds,
      studentRelease: DropdownLists.studentRelease,
      user: null,
      selectedVendor: null,
      vendorData: [],
      regularRouteVendorId: null,
      regularRouteVehicleId: null,
      regularRouteLookupKey: null,
      vehicleList: [],
      supervisors: [],
      formattedSupervisors: [],
      selectedSchool: null,
      vehicleData: [],
      vehicle:'',
    }
  },
  computed: {
    formattedGradeList() {
      return Object.keys(this.gradeList).map(key => {
        return {
          value: parseInt(key),
          label: this.gradeList[key]
        }
      })
    },
    vendorOptions() {
      return Array.isArray(this.vendorData)
        ? this.vendorData.map(vendor => ({
            value: vendor.id,
            label: `${vendor.abbreviation} - ${vendor.partition.description}`,
            }))
        : []
    },
    vehicleOptions() {
      return Array.isArray(this.vehicleData)
        ? this.vehicleData.map(vehicle => ({
            value: vehicle.id,
            label: `${vehicle.code}`,
          }))
        : []
    },
    canApproveOneTimeRide() {
      return this.user?.can('approve_one_time_ride') || false
    },

    scheduledReplacementTrip() {
      if (!this.formValues.tripDate || !this.inferredRoute) {
        return null
      }

      const scheduleWeekdays = this.defaultValues?.scheduleWeekdays

      if (!Array.isArray(scheduleWeekdays)) {
        return null
      }

      const weekdayNames = [
        'sun',
        'mon',
        'tue',
        'wed',
        'thu',
        'fri',
        'sat',
      ]

      const tripDate = new Date(`${this.formValues.tripDate}T00:00:00`)
      const weekday = weekdayNames[tripDate.getDay()]

      const scheduleDay = scheduleWeekdays.find(
        day => day.week === '0' && day.weekday === weekday
      )

      if (!scheduleDay) {
        return null
      }

      if (this.inferredRoute === 'inbound' && scheduleDay.pickup_route) {
        return {
          direction: 'inbound',
          route: scheduleDay.pickup_route,
          time: scheduleDay.pickup_time,
        }
      }

      if (this.inferredRoute === 'outbound' && scheduleDay.dropoff_route) {
        return {
          direction: 'outbound',
          route: scheduleDay.dropoff_route,
          time: scheduleDay.dropoff_time,
        }
      }

      return null
    },
    inferredRoute() {
      const pickup = this.formValues.pickupLocationType
      const dropoff = this.formValues.dropoffLocationType

      if (pickup === 'home' && dropoff === 'school') {
        return 'inbound'
      }

      if (pickup === 'school' && dropoff === 'home') {
        return 'outbound'
      }

      return this.formValues.route || null
    },

    needsRouteDirection() {
      return (
        this.formValues.pickupLocationType === 'other' ||
        this.formValues.dropoffLocationType === 'other'
      )
    },
    regularRouteVendorName() {
      if (this.regularRouteVendorId == null) return null

      const vendor = this.vendorData.find(
        v => String(v.id) === String(this.regularRouteVendorId)
      )

      return vendor?.name || vendor?.abbreviation || null
    },

    formattedTripDate() {
      if (!this.formValues.tripDate) return ''

      return new Date(
        `${this.formValues.tripDate}T00:00:00`
      ).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    },
  },
  methods: {
    today() {
      var date = new Date()
      var month = date.getMonth() + Number(1)
      if (month < 10) {
        month = '0'+month
      }
      return date.getFullYear() +'-'+ month +'-'+ date.getDate()
    },
    maxDate() {
      var date = new Date()
      date.setDate(date.getDate() + 14)
      var month = date.getMonth() + 1
      var day = date.getDate()
      if (month < 10) {
        month = '0' + month
      }
      if (day < 10) {
        day = '0' + day
      }
      return date.getFullYear() + '-' + month + '-' + day
    },
    setDefault() {
      if (this.defaultValues) {
        this.setSpecialNeeds()
      }
    },
    displayFormHasErrors(errorsMap) {
      return Object.values(errorsMap).flat().length > 0
    },
    specialChange(value) {
      if (this.suppressBoosterSeatWatch) {
        this.suppressBoosterSeatWatch = false
      }
      if (value && value.needsList && Array.isArray(value.needsList)) {
        this.$emit('boosterSeatSelected', value.needsList.indexOf('infoBoosterSeat') > -1)
      } else {
        console.error('Expected value to have a needsList array, but got:', value)
      }
    },
    setSpecialNeeds() {
      this.defaultNeed.specialNeeds = { needsList: [] }
      const needsList = [
        'infoAide',
        'infoWheelchair',
        'infoSolo',
        'infoEnclosedVehicle',
        'infoBoosterSeat',
        'infoSafetyVest',
        'infoSafetyBeltLockingCover',
        'infoCarSeat',
      ]
      needsList.forEach((need) => {
        if (this.defaultValues[need]) {
          this.defaultNeed.specialNeeds.needsList.push(need)
          if (need === 'infoAide') {
            this.defaultNeed.specialNeeds.aide = this.defaultValues.infoAide
            this.defaultNeed.specialNeeds.aideOnetoOne =
              this.defaultValues.aide === 'A1:1'
          }
        }
      })
      this.defaultNeed.specialNeeds = { ...this.defaultNeed.specialNeeds }
    },
    onGradeChange() {
      this.$emit('gradeSelected', parseInt(this.formValues.grade))
    },
    async fetchSupervisors() {
      try {
        const supervisors = await oneTimeRideFormService.getSupervisorList()
        this.supervisors = supervisors
        this.formattedSupervisors = this.supervisors.map(supervisor => ({
          label: `${supervisor.first_name} ${supervisor.last_name}`,
          value: supervisor.id.toString(),
        }))
      } catch (error) {
        console.error('Error fetching supervisors:', error)
      }
    },
    async fetchVendors() {
      try {
        const response = await vendors.getAllVendors()
        if (response && Array.isArray(response.data)) {
          this.vendorData = response.data
        } else {
          console.error('Fetched data is not an array:', response)
        }
      } catch (error) {
        console.error('Failed to fetch vendor data:', error)
      }
    },
    async fetchVehicles(defaultVehicleId = null) {
      const vendorId = this.formValues.selectedVendor?.value

      this.formValues.selectedVehicle = ''
      this.vehicleData = []

      if (!vendorId) return

      try {
        const response = await vendors.getVehiclesFromVendor(vendorId)

        // Ignore outdated responses if the user changed vendors.
        if (String(this.formValues.selectedVendor?.value) !== String(vendorId)) {
          return
        }

        if (response && Array.isArray(response.data)) {
          this.vehicleData = response.data

          if (defaultVehicleId != null) {
            const vehicle = this.vehicleOptions.find(
              v => String(v.value) === String(defaultVehicleId)
            )

            if (vehicle) {
              this.formValues.selectedVehicle = vehicle
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch vehicle data:', error)
      }
    },
    onVendorChanged() {
      const selectedVendorId = this.formValues.selectedVendor?.value

      // Don't reload if the vendor hasn't actually changed.
      if (
        String(selectedVendorId) === String(this.regularRouteVendorId)
      ) {
        return
      }

      this.fetchVehicles()
    },
    async fetchDistricts() {
      try {
        const districts = await oneTimeRideFormService.getDistrict()

        if (Array.isArray(districts)) {
          this.districtList = districts.map(district => ({
            label: `${district.code}`,
            value: district.code,
          }))
        } else {
          console.error('Districts data is not in expected format:', districts)
        }
      } catch (error) {
        console.error('Error fetching districts:', error)
      }
    },
    async fetchBuildings() {
      try {
        const buildings = await oneTimeRideFormService.getBuilding()
        if (Array.isArray(buildings)) {
          this.schoolList = buildings.map(building => ({
            label: `${building.name}`,
            value: building.name,
            address: building.address,
          }));

          if (this.defaultValues && this.defaultValues.building) {
            this.selectedSchool = this.defaultValues.building.name
          }
        } else {
          console.error('Buildings data is not in expected format:', buildings)
        }
      } catch (error) {
        console.error('Error fetching buildings:', error)
      }
    },
    setSchool() {
      this.formValues.school = this.selectedSchool
      this.updateValue()
    },
    setDistrict() {
      this.formValues.requestingDistrict = this.selectedDistrict || null
      this.updateValue()
    },
    setDefaults() {
      Object.keys(this.formValues).forEach((key) => {
        if (this.defaultValues[key] !== undefined) {
          this.formValues[key] = this.defaultValues[key]
        }
      })

      const districtCode = this.defaultValues.district?.code || null

      this.formValues.requestingDistrict = districtCode
      this.selectedDistrict = districtCode
        ? { label: districtCode, value: districtCode }
        : {}
      if (this.formValues.pickupLocationType === 'home') {
        this.pickupDefaultAddress = {
          ...this.defaultValues.address
        }

        this.formValues.pickupAddress = {
          ...this.defaultValues.address
        }
      }

      if (this.formValues.dropoffLocationType === 'school') {
        this.dropoffDefaultAddress = this.defaultValues.building?.address
          ? { ...this.defaultValues.building.address }
          : {}

        this.formValues.dropoffAddress = this.defaultValues.building?.address
          ? { ...this.defaultValues.building.address }
          : {}

        this.formValues.dropoffLocationName =
          this.defaultValues.building?.name || ''
      }
    },
    updateValue() {
      this.$emit('input', this.formValues)
    },
    formatTime(time) {
      if (!time) return ''

      const [hours, minutes] = time.split(':')
      const date = new Date()
      date.setHours(Number(hours), Number(minutes), 0)

      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      })
    },
    async fetchRegularRouteVendor(trip) {
      const routeId = trip?.route?.db_id
      const tripDate = this.formValues.tripDate

      const lookupKey = routeId && tripDate
        ? `${routeId}:${tripDate}`
        : null

      this.regularRouteLookupKey = lookupKey
      this.regularRouteVendorId = null
      this.regularRouteVehicleId = null

      if (!lookupKey) return

      try {
        const response = await getRouteInfo(routeId)

        // Ignore responses for a previously selected route/date.
        if (this.regularRouteLookupKey !== lookupKey) return

        const routeInfos = response.data || []

        const routeInfo = routeInfos
          .filter(info => info.start_date <= tripDate)
          .sort((a, b) => b.start_date.localeCompare(a.start_date))[0]

        this.regularRouteVehicleId = routeInfo?.vehicle?.id ?? null
        this.regularRouteVendorId = routeInfo?.vendor_id ?? null
      } catch (error) {
        if (this.regularRouteLookupKey === lookupKey) {
          console.error('Failed to fetch regular route vendor:', error)
          this.regularRouteVendorId = null
          this.regularRouteVehicleId = null
        }
      }
    },
    syncFaultVendor() {
      const vendor = this.vendorOptions.find(
        v => String(v.value) === String(this.regularRouteVendorId)
      )

      if (vendor && this.regularRouteVendorId != null) {
        const vendorChanged =
          String(this.formValues.selectedVendor?.value) !== String(vendor.value)

        this.formValues.selectedVendor = vendor

        if (vendorChanged || !this.formValues.selectedVehicle?.value) {
          this.fetchVehicles(this.regularRouteVehicleId)
        }
      } else {
        this.formValues.selectedVendor = ''
        this.formValues.selectedVehicle = ''
        this.vehicleData = []
      }
    },
    async onSubmit(formInvalid) {
      let hasError = this.scrollToFirstError()
      if (formInvalid || hasError) {
        return false
      }
      try {
        const formData = {
          student: this.defaultValues.dbId,
          is_inbound: this.inferredRoute === 'inbound',
          replaces_inbound_route:
            this.formValues.replacesRegularRoute &&
            this.inferredRoute === 'inbound',
          replaces_outbound_route:
            this.formValues.replacesRegularRoute &&
            this.inferredRoute === 'outbound',
          requesting_district: this.formValues.requestingDistrict,
          trip_date: this.formValues.tripDate,
          alternate_service_provider: this.formValues.alternateServiceProvider,
          vendor_designated: this.formValues.vendorDesignated,
          first_name: this.formValues.firstName,
          last_name: this.formValues.lastName,
          grade: this.formValues.grade,
          pickup_address: this.formValues.pickupAddress,
          dropoff_address: this.formValues.dropoffAddress,
          pickup_location_name: this.formValues.pickupLocationName,
          dropoff_location_name: this.formValues.dropoffLocationName,
          pickup_other_information: this.formValues.pickupOtherInformation,
          dropoff_other_information: this.formValues.dropoffOtherInformation, 
          school: this.formValues.school,
          pickup_time: this.formValues.pickupTime,
          dropoff_time: this.formValues.dropoffTime,
          vendor_notes: this.formValues.vendorNotes,
          special_needs: this.formValues.specialNeeds,
          student_release: this.formValues.studentRelease,
          supervisor: this.canApproveOneTimeRide ? null : this.formValues.supervisor,
          request_reason: this.formValues.requestReason,
          notified: this.formValues.notified,
          link_to_communication: this.formValues.linkToCommunication,
          covering_cost: this.formValues.coveringCost,
          district_rep: this.formValues.districtRep,
          selected_vendor: this.formValues.coveringCost === 'CST/VENDOR' ? (this.formValues.selectedVendor?.value ?? null) : null,
          selected_vehicle: this.formValues.selectedVehicle.value,
          route: this.inferredRoute,
        }
        const data = await oneTimeRideFormService.postNewOneTimeRideForm(formData)
        this.$emit('formSubmitted', data)
        this.$router.push({ name: 'StudentList' })
      } catch (error) {
        console.error('Error submitting form:', error)
      }
    },
    validateProvidedInfo() {
      this.showCstInternalData = oneTimeRideFormService.canViewInternalCstData(this.user)
      this.canSubmit = oneTimeRideFormService.canSubmitForm(this.user)
    },
    checkUserCanViewPage() {
      if (!this.user.can('view_one_time_ride')) {
        this.$router.push({
          name: 'StudentList'
        })
        return true
      }
    },
    scrollToFirstError() {
      const elems = document.getElementsByName('error-text')
      let hasError = false

      for(let el of Array.from(elems)) {
        if (el.innerText && el.innerText.trim()) {
          hasError = true
          el.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
          })
          break
        }
      }
      return hasError
    },  
  },
  async created() {
    await this.fetchVendors()
    this.fetchSupervisors()
    this.fetchBuildings()
    this.setDefaults()
    this.fetchDistricts()
  },
  async mounted() {
    this.user = await user.get()
    if (this.checkUserCanViewPage()) {
      return
    }
    this.validateProvidedInfo()
    this.setDefault()
    this.updateValue()
  },
  watch: {

    defaultValues() {
      this.setSpecialNeeds()
      this.setDefaults()
    },
    formValues() {
      this.updateValue()
    },
    selectedGrade(newGrade) {
      this.formValues.grade = newGrade
    },
    selectedSchool(newSchool) {
      this.formValues.school = newSchool
    },
    'formValues.pickupLocationType'(value) {
      if (value === 'home') {
        const address = {
          ...this.defaultValues.address
        }

        this.pickupDefaultAddress = address
        this.formValues.pickupAddress = address
        this.formValues.pickupLocationName = ''
      }

      if (value === 'school') {
        const address = {
          ...this.defaultValues.building.address
        }

        this.pickupDefaultAddress = address
        this.formValues.pickupAddress = address
        this.formValues.pickupLocationName =
          this.defaultValues.building.name || ''
      }

      if (value === 'other') {
        this.pickupDefaultAddress = {}
        this.formValues.pickupAddress = {}
        this.formValues.pickupLocationName = ''
      }
    },
    'formValues.dropoffLocationType'(value) {
      if (value === 'home') {
        const address = {
          ...this.defaultValues.address
        }

        this.dropoffDefaultAddress = address
        this.formValues.dropoffAddress = address
        this.formValues.dropoffLocationName = ''
      }

      if (value === 'school') {
        const address = {
          ...this.defaultValues.building.address
        }

        this.dropoffDefaultAddress = address
        this.formValues.dropoffAddress = address
        this.formValues.dropoffLocationName =
          this.defaultValues.building.name || ''
      }

      if (value === 'other') {
        this.dropoffDefaultAddress = {}
        this.formValues.dropoffAddress = {}
        this.formValues.dropoffLocationName = ''
      }
    },
    scheduledReplacementTrip(newTrip) {
      if (!newTrip) {
        this.formValues.replacesRegularRoute = false
      }

      this.fetchRegularRouteVendor(newTrip)
    },
    regularRouteVendorId(vendorId) {
      if (vendorId == null) return

      const vendor = this.vendorOptions.find(
        v => String(v.value) === String(vendorId)
      )

      if (vendor) {
        this.formValues.selectedVendor = vendor
        this.fetchVehicles(this.regularRouteVehicleId)
      }
    },
  }
}
</script>
